if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (!this.form || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          !this.submitButton ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        try {
          const response = await fetch(routes.cart_add_url, config);
          const responseData = await response.json();

          if (responseData.status) {
            await this.publishCartError(formData, responseData);

            this.handleErrorMessage(
              responseData.description ||
              responseData.message ||
              'Unable to add this product to the cart.'
            );

            const soldOutMessage =
              this.submitButton.querySelector('.sold-out-message');

            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          /*
           * Ważne:
           * czekamy na zakończenie subskrybentów cartUpdate,
           * zanim Dawn wyrenderuje cart drawer i licznik koszyka.
           */
          await this.publishCartUpdate(formData, responseData);

          this.error = false;

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.renderCartContents(responseData);
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            this.renderCartContents(responseData);
          }
        } catch (error) {
          console.error('Add to cart error:', error);

          this.handleErrorMessage(
            'The product may have been added, but the cart could not be refreshed. Please open the cart to check.'
          );
        } finally {
          this.submitButton.classList.remove('loading');

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
          }

          spinner?.classList.add('hidden');

          if (
            typeof CartPerformance !== 'undefined' &&
            typeof CartPerformance.measureFromEvent === 'function'
          ) {
            CartPerformance.measureFromEvent('add:user-action', evt);
          }
        }
      }

      async publishCartUpdate(formData, responseData) {
        if (
          typeof publish !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined' ||
          !PUB_SUB_EVENTS.cartUpdate
        ) {
          return;
        }

        let startMarker = null;

        if (
          typeof CartPerformance !== 'undefined' &&
          typeof CartPerformance.createStartingMarker === 'function'
        ) {
          startMarker = CartPerformance.createStartingMarker(
            'add:wait-for-subscribers'
          );
        }

        try {
          await Promise.resolve(
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: responseData,
            })
          );
        } catch (error) {
          /*
           * Błąd pojedynczego subskrybenta nie może zatrzymać
           * renderowania koszyka po udanym dodaniu produktu.
           */
          console.warn('Cart update subscriber error:', error);
        }

        if (
          startMarker &&
          typeof CartPerformance !== 'undefined' &&
          typeof CartPerformance.measureFromMarker === 'function'
        ) {
          CartPerformance.measureFromMarker(
            'add:wait-for-subscribers',
            startMarker
          );
        }
      }

      async publishCartError(formData, responseData) {
        if (
          typeof publish !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined' ||
          !PUB_SUB_EVENTS.cartError
        ) {
          return;
        }

        try {
          await Promise.resolve(
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors:
                responseData.errors ||
                responseData.description,
              message: responseData.message,
            })
          );
        } catch (error) {
          console.warn('Cart error subscriber error:', error);
        }
      }

      renderCartContents(responseData) {
        if (!this.cart || typeof this.cart.renderContents !== 'function') {
          window.location = window.routes.cart_url;
          return;
        }

        const render = () => {
          this.cart.renderContents(responseData);
        };

        if (
          typeof CartPerformance !== 'undefined' &&
          typeof CartPerformance.measure === 'function'
        ) {
          CartPerformance.measure(
            'add:paint-updated-sections',
            render
          );
        } else {
          render();
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');

          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}